/**
 * What the interface calls Korea, in Korean.
 *
 * This app writes 조선, not 한국 — and 조선어 rather than 한국어 with it, since
 * that word contains this one. It is a naming decision about the product's own
 * chrome and answers, applied to what is DRAWN.
 *
 * ── Why this is a display-layer rewrite ──────────────────────────────────────
 *
 * The obvious place to put it is the server, on the way out of the model, and
 * that is the wrong place for three reasons that only become clear once you try
 * it:
 *
 *   • **The stream splits mid-word.** A server filter sees the answer in
 *     tokeniser-sized pieces, and `한국` genuinely arrives as `한` then `국`. A
 *     per-chunk rewrite passes both halves through untouched, so the filter has
 *     to buffer a tail across chunk boundaries and remember to flush it. The
 *     browser has no such problem: by the time anything is drawn it is holding
 *     the whole answer as one string.
 *
 *   • **It would edit the record.** The cache, the history and the console's
 *     request log are meant to hold what the model actually said. A server-side
 *     rewrite quietly makes them hold something else, and the one place you
 *     would go to find out what really came back is the place that can no longer
 *     tell you.
 *
 *   • **It would have to be remembered.** Every route that can return Korean
 *     prose would need to opt in, including the ones nobody has written yet.
 *     Here there is exactly one pipeline into the answer pane, and it goes
 *     through this function.
 *
 * The model is never asked to do this, which is the other half of the same
 * point: an instruction in the prompt competes with the rest of the prompt and
 * loses some of the time, costs tokens on every request in every language, and
 * gives a different answer depending on the model. A string replacement is the
 * same every time.
 *
 * ── The property that makes it safe ──────────────────────────────────────────
 *
 * Every replacement below is the SAME LENGTH as what it replaces — two
 * characters for two. That is not a coincidence worth ignoring: the answer pane
 * maps its laid-out segments back to the source lines they came from by offset
 * (see `LinkedAnswer` and lib/textShape.ts), and a rewrite that changed the
 * length would slide every one of those mappings out of place. Any term added
 * here must keep that property, or the hover-linking breaks in a way that looks
 * like a layout bug rather than a text one.
 */

/**
 * The rewrites.
 *
 * One entry does the work of several: 한국 is a prefix of 한국어 (the language),
 * 한국말 (the spoken language) and 한국인 (a person), so replacing it settles all
 * of them and there is nothing to keep in sync.
 *
 * What it does NOT reach is 대한민국 — the official name — because the two
 * characters are not adjacent in it (한 is followed by 민). That is left as it
 * is rather than special-cased: it is a formal proper noun rather than the
 * everyday word this is about, and the replacement for it would be a different
 * name entirely, not a different spelling of the same one.
 *
 * 한글 is untouched for the same kind of reason — it is the alphabet, not the
 * country or the language written in it — and does not contain 한국 anyway.
 */
const TERMS: [from: string, to: string][] = [['한국', '조선']];

/** Rewrite a string for display. Safe on text with no Korean in it at all. */
export function preferredTerms(text: string): string {
  if (!text) return text;
  let out = text;
  for (const [from, to] of TERMS) out = out.split(from).join(to);
  return out;
}

/**
 * The same rewrite, through a structure.
 *
 * For the answers that arrive as an object rather than a string — the
 * dictionary entry, whose senses, explanation and examples are five separate
 * fields. Walking it means a field added to that entry later is covered without
 * anybody remembering to come back here.
 *
 * Returns the input unchanged when there is nothing to rewrite, so a render
 * that depends on object identity is not forced to re-run by this.
 */
export function preferredTermsDeep<T>(value: T): T {
  if (typeof value === 'string') return preferredTerms(value) as unknown as T;
  if (Array.isArray(value)) {
    const next = value.map(preferredTermsDeep);
    return (next.some((v, i) => v !== value[i]) ? next : value) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const src = value as Record<string, unknown>;
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const k of Object.keys(src)) {
      next[k] = preferredTermsDeep(src[k]);
      if (next[k] !== src[k]) changed = true;
    }
    return (changed ? next : value) as unknown as T;
  }
  return value;
}
